import React, { useEffect, useMemo, useState, useRef } from "react";
import Chart from "react-apexcharts";
import style from "./chart.module.css";
import DateInput from "../../../../common/UI/Inputs/InputDate";
import styleForm from "../../../../common/theme/styles/form.module.css";

type ApexChartType =
  | "line"
  | "area"
  | "bar"
  | "pie"
  | "donut"
  | "radialBar"
  | "scatter"
  | "bubble"
  | "heatmap"
  | "candlestick"
  | "boxPlot"
  | "radar"
  | "polarArea"
  | "rangeBar"
  | "rangeArea"
  | "treemap";

const DEFAULT_TYPES: ApexChartType[] = ["area", "line", "bar"];

const TYPE_LABELS: Record<ApexChartType, string> = {
  line: "Línea",
  area: "Área",
  bar: "Barra",
  pie: "Pie",
  donut: "Donut",
  radialBar: "Radial",
  scatter: "Scatter",
  bubble: "Bubble",
  heatmap: "Heatmap",
  candlestick: "Candlestick",
  boxPlot: "Box Plot",
  radar: "Radar",
  polarArea: "Polar",
  rangeBar: "Range Bar",
  rangeArea: "Range Area",
  treemap: "Treemap",
};

interface ChartComponentProps {
  typeGraph?: ApexChartType;
  /** Lista de tipos que el usuario podrá elegir en el select */
  selectableTypes?: ApexChartType[];
  dataDashboard?: Array<{ name: string; data: number[] }>;
  dataOptions?: any;
  onDateChange?: (dateRange: [Date, Date] | null) => void;
  titleChart?: string;
  emptyMessage?: string;
}

const ChartComponent: React.FC<ChartComponentProps> = ({
  typeGraph = "area",
  selectableTypes = DEFAULT_TYPES,
  dataDashboard = [],
  dataOptions = {},
  titleChart = "",
  onDateChange,
  emptyMessage = "No hay datos para el rango seleccionado.",
}) => {
  // Normalizar "hoy"
  const today = new Date();
  const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const defaultEnd = normalizedToday;
  const defaultStart = new Date(defaultEnd);
  defaultStart.setDate(defaultEnd.getDate() - 6);

  const [chartType, setChartType] = useState<ApexChartType>(typeGraph);
  const [currentPalette, setCurrentPalette] = useState<string>("palette1");
  const [localStartDate, setLocalStartDate] = useState<Date>(defaultStart);
  const [localEndDate, setLocalEndDate] = useState<Date>(defaultEnd);

  const initCalledRef = useRef(false);

  // Asegurar que siempre esté disponible el tipo inicial aunque no venga en selectableTypes
  const effectiveTypes = useMemo(() => {
    const set = new Set<ApexChartType>(selectableTypes);
    set.add(typeGraph);
    return Array.from(set);
  }, [selectableTypes, typeGraph]);

  useEffect(() => {
    if (!initCalledRef.current) {
      initCalledRef.current = true;
      onDateChange?.([defaultStart, defaultEnd]);
    }
  }, [defaultStart, defaultEnd, onDateChange]);

  // Si cambia la lista de tipos y el actual ya no está, forzar uno válido
  useEffect(() => {
    if (!effectiveTypes.includes(chartType)) {
      setChartType(effectiveTypes[0]);
    }
  }, [effectiveTypes, chartType]);

  const sanitizedSeries = Array.isArray(dataDashboard) ? dataDashboard : [];

  const isValidData =
    sanitizedSeries.length > 0 &&
    sanitizedSeries.some(
      (series) =>
        Array.isArray(series.data) &&
        series.data.length > 0 &&
        series.data.some((d) => d !== null && d !== undefined && !isNaN(Number(d)))
    );

  const dataOptionsGraph = useMemo(
    () => ({
      ...dataOptions,
      chart: {
        ...(dataOptions.chart || {}),
        type: chartType,
        zoom: { enabled: true, type: "x", autoScaleYaxis: true },
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
          autoSelected: "zoom",
        },
      },
      theme: {
        ...(dataOptions.theme || {}),
        palette: currentPalette,
      },
      noData: {
        text: emptyMessage,
        align: "center",
        verticalAlign: "middle",
        style: {
          fontSize: "14px",
          fontWeight: 500,
        },
      },
    }),
    [dataOptions, chartType, currentPalette, emptyMessage]
  );

  return (
    <div className={style.containerGraph}>
      {titleChart && (
        <h4 className={style.chartTitle} style={{ textAlign: "center" }}>
          {titleChart}
        </h4>
      )}

      <div className={style.contentHeader} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div className={style.controls}>
          <div className={styleForm.container__input} style={{ minWidth: "70px" }}>
            <label className={styleForm.input__label}>Paleta</label>
            <select
              style={{ minWidth: "70px" }}
              className={styleForm.input__input}
              value={currentPalette}
              onChange={(e) => setCurrentPalette(e.target.value)}
            >
              {Array.from({ length: 10 }, (_, i) => (
                <option key={i + 1} value={`palette${i + 1}`}>
                  paleta {i + 1}
                </option>
              ))}
            </select>
          </div>

          <div className={styleForm.container__input} style={{ minWidth: "70px" }}>
            <label className={styleForm.input__label}>Tipo</label>
            <select
            style={{ minWidth: "70px" }}
              className={styleForm.input__input}
              value={chartType}
              onChange={(e) => setChartType(e.target.value as ApexChartType)}
            >
              {effectiveTypes.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t] ?? t}
                </option>
              ))}
            </select>
          </div>

          <div className={style.controlGroup} style={{ maxWidth: 260 }}>
            <DateInput
              textLabel="Seleccionar rango fechas"
              name="fechaChart"
              range
              initialValue={[localStartDate, localEndDate]}
              onChange={(dates) => {
                if (Array.isArray(dates) && dates.length === 2) {
                  setLocalStartDate(dates[0]);
                  setLocalEndDate(dates[1]);
                  onDateChange?.(dates as [Date, Date]);
                } else {
                  const now = new Date();
                  const normalizedNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  const weekStart = new Date(normalizedNow);
                  weekStart.setDate(weekStart.getDate() - 6);
                  setLocalStartDate(weekStart);
                  setLocalEndDate(normalizedNow);
                  onDateChange?.([weekStart, normalizedNow]);
                }
              }}
              dateFormat="dd-MM-yyyy"
            />
          </div>
        </div>
      </div>

      <div className={style.contentBody} style={{ position: "relative" }}>
        {isValidData ? (
          <Chart
            key={`${chartType}-${currentPalette}`}
            options={dataOptionsGraph}
            series={sanitizedSeries}
            type={chartType}
            width="100%"
            height="350"
          />
        ) : (
          <div className={style.emptyState}>
            <p style={{ margin: "40px 0 8px", textAlign: "center" }}>{emptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartComponent;
